<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="close">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow" :class="statusTone">
                  <span class="status-dot"></span>
                  {{ statusLabel }}
                </div>
                <h2>{{ money(payment.amount, payment.currency) }}</h2>
                <p>{{ accountName }}<template v-if="planName"> · {{ planName }}</template></p>
              </div>
              <button type="button" class="icon-button" v-on:click="close" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <div class="modal-body">
              <section class="form-section">
                <h3>Account</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Account holder</dt>
                    <dd>{{ accountName }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Email</dt>
                    <dd>{{ accountEmail || "—" }}</dd>
                  </div>
                  <div class="detail full">
                    <dt>Organization</dt>
                    <dd>{{ organizationName || "—" }}</dd>
                  </div>
                </dl>
              </section>

              <section class="form-section">
                <h3>Payment</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Amount</dt>
                    <dd>{{ money(payment.amount, payment.currency) }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Status</dt>
                    <dd><span class="badge" :class="statusBadgeClass">{{ statusLabel }}</span></dd>
                  </div>
                  <div class="detail">
                    <dt>Paid</dt>
                    <dd>{{ payment.isPaid ? "Yes" : "No" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Method</dt>
                    <dd>{{ payment.paymentMethod ? payment.paymentMethod.name : "—" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Plan</dt>
                    <dd>{{ planName || "—" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Payment date</dt>
                    <dd>{{ payment.paymentDate ? $filters.getDateAndTime(payment.paymentDate) : "—" }}</dd>
                  </div>
                  <div class="detail full">
                    <dt>Tx reference</dt>
                    <dd class="mono">{{ payment.txRef || "—" }}</dd>
                  </div>
                  <div class="detail full">
                    <dt>Chapa reference</dt>
                    <dd class="mono">{{ payment.chapaReference || "—" }}</dd>
                  </div>
                  <div v-if="payment.transactionReference" class="detail full">
                    <dt>Bank reference</dt>
                    <dd class="mono">{{ payment.transactionReference }}</dd>
                  </div>
                </dl>
              </section>

              <section class="form-section">
                <h3>Audit</h3>
                <dl class="detail-grid">
                  <div class="detail">
                    <dt>Recorded by</dt>
                    <dd>{{ recordedBy || "—" }}</dd>
                  </div>
                  <div class="detail">
                    <dt>Created</dt>
                    <dd>{{ $filters.getDateAndTime(payment.createdAt) }}</dd>
                  </div>
                </dl>
              </section>
            </div>

            <footer class="modal-foot">
              <button type="button" class="ghost-button" v-on:click="close">Close</button>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { PAYMENT_STATUS } from "@/utils/enums";
import { DEFAULT_CURRENCY } from "@/utils/const";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

const STATUS_BADGE = {
  [PAYMENT_STATUS.success]: "badge-success",
  [PAYMENT_STATUS.pending]: "badge-warning",
  [PAYMENT_STATUS.failed]: "badge-danger",
  [PAYMENT_STATUS.cancelled]: "badge-secondary",
};

const STATUS_TONE = {
  [PAYMENT_STATUS.success]: "tone-verified",
  [PAYMENT_STATUS.pending]: "tone-pending",
  [PAYMENT_STATUS.failed]: "tone-rejected",
  [PAYMENT_STATUS.cancelled]: "tone-failed",
};

export default {
  name: "PaymentDetailModal",
  props: {
    payment: { type: Object, required: true },
  },
  emits: ["close"],
  data() {
    return {
      showModal: true,
    };
  },
  computed: {
    organization() {
      return this.payment.organization || null;
    },
    organizationName() {
      return this.organization ? this.organization.name : "";
    },
    // The org's owner is the account the payment is for.
    owner() {
      return this.organization && this.organization.user ? this.organization.user : null;
    },
    accountName() {
      if (this.owner) return this.$filters.fullName(this.owner.fname, this.owner.lname) || "—";
      if (this.payment.createdByUser) {
        return this.$filters.fullName(this.payment.createdByUser.fname, this.payment.createdByUser.lname) || "—";
      }
      return this.organizationName || "—";
    },
    accountEmail() {
      if (this.owner && this.owner.email) return this.owner.email;
      return this.organization ? this.organization.email : "";
    },
    planName() {
      return this.payment.subscriptionPlan ? this.payment.subscriptionPlan.name : "";
    },
    recordedBy() {
      const u = this.payment.createdByUser;
      return u ? this.$filters.fullName(u.fname, u.lname) : "";
    },
    statusLabel() {
      const s = this.payment.status || "";
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
    },
    statusBadgeClass() {
      return STATUS_BADGE[this.payment.status] || "badge-secondary";
    },
    statusTone() {
      return STATUS_TONE[this.payment.status] || "tone-failed";
    },
  },
  methods: {
    money(value, currency) {
      const amount = Number(value);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, currency || DEFAULT_CURRENCY);
    },
    close() {
      this.$emit("close");
    },
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
  },
  mounted() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow || "";
    window.removeEventListener("keydown", this.onKeydown);
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  width: 520px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--ehs-color-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--ehs-color-ink);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--ehs-color-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ehs-color-soft);
}

.tone-verified { background: var(--ehs-color-success-soft); color: #047857; border-color: transparent; }
.tone-verified .status-dot { background: var(--ehs-color-success); }
.tone-pending { background: var(--ehs-color-warning-soft); color: #92400e; border-color: transparent; }
.tone-pending .status-dot { background: var(--ehs-color-warning); }
.tone-rejected { background: var(--ehs-color-danger-soft); color: #b91c1c; border-color: transparent; }
.tone-rejected .status-dot { background: var(--ehs-color-danger); }
.tone-failed { background: var(--ehs-color-canvas-strong); color: var(--ehs-color-text); border-color: transparent; }

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.84rem;
  text-transform: capitalize;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ehs-color-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--ehs-color-ink);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-hover);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--ehs-color-canvas);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
  margin: 0;
}

.detail.full {
  grid-column: 1 / -1;
}

.detail dt {
  margin: 0 0 3px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--ehs-color-soft);
}

.detail dd {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--ehs-color-ink);
  word-break: break-word;
  text-transform: capitalize;
}

.detail dd.mono {
  font-family: var(--ehs-font-mono);
  font-size: 0.82rem;
  text-transform: none;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--ehs-color-border);
  background: #ffffff;
}

.ghost-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid var(--ehs-color-border);
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  background: #ffffff;
  color: var(--ehs-color-ink);
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button:hover {
  background: var(--ehs-color-canvas);
  border-color: var(--ehs-color-border-strong);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    flex-wrap: wrap;
  }
}
</style>
